import type { ReactElement } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export interface FaqItem {
  q: string;
  a: string;
}

interface FaqProps {
  items?: FaqItem[];
  heading?: string;
  eyebrow?: string;
}

const DEFAULT_FAQ: FaqItem[] = [
  {
    q: "What does Chill cost?",
    a: "From $25/mo (or $250/yr) after a 14-day free trial. That buys 100 credits a month — one credit is roughly one change — and you can pick a bigger bundle if you need more.",
  },
  {
    q: "What if I cancel during the trial?",
    a: "You are not charged. Cancel anytime before the 14 days are up, from your billing page, and it ends there — no card is charged and no credits carry a bill.",
  },
  {
    q: "Do I need a custom domain?",
    a: "No. You start at <your-slug>.getchill.ai for free. Bring your own domain anytime — SSL is included.",
  },
  {
    q: "What can Chill do?",
    a: "Update copy and images; add events or offerings; change hours, links, forms, and contact details — by text or email.",
  },
  {
    q: "Can I edit my site directly?",
    a: "Tell Chill what you want by text or email. There is no visual editor to learn.",
  },
  {
    q: "How is this different from Squarespace or Wix?",
    a: "Those give you an editor to operate. Chill turns a message into the change — text or email, then it is on your site.",
  },
  {
    q: "Can I cancel after the trial?",
    a: "Anytime, from your billing page. Cancelling stops the next charge; it does not refund the current billing period.",
  },
];

export function Faq({
  items = DEFAULT_FAQ,
  heading = "Questions, answered.",
  eyebrow = "FAQ",
}: FaqProps): ReactElement {
  return (
    <section className="py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[0.72fr_1.28fr] lg:gap-24">
        <div>
          <div className="marketing-kicker text-[#45635e]">
            {eyebrow}
          </div>
          <h2 className="marketing-display mt-5 max-w-sm text-5xl leading-[1.02] text-[#12211d] sm:text-6xl">
            {heading}
          </h2>
        </div>
        <Accordion
          type="single"
          collapsible
          className="w-full border-t border-[#d9e3e0]"
        >
          {items.map((it, i) => (
            <AccordionItem
              key={it.q}
              value={`item-${i}`}
              className="border-[#d9e3e0]"
            >
              <AccordionTrigger className="py-6 text-base font-medium text-[#1d2c28] hover:no-underline">
                {it.q}
              </AccordionTrigger>
              <AccordionContent className="max-w-2xl pb-6 text-sm leading-6 text-[#65736f]">
                {it.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
